import { createUseStyles } from "react-jss";
import * as Mixins from "../../../theme/Mixins";
import { Theme } from "../../../types/Theme";
import {
    CHOICE_FOCUS_OUTLINE_OFFSET_PX,
    resolveModernChoiceFocusOutline,
} from "../../../theme/choiceThemeTokens";
import {
    MODERN_FILE_UPLOAD_ACTION_BUTTON_SIZE_PX,
    MODERN_FILE_UPLOAD_BORDER_WIDTH_PX,
    MODERN_FILE_UPLOAD_BROWSE_BUTTON_HEIGHT_PX,
    MODERN_FILE_UPLOAD_BROWSE_BUTTON_PADDING_X_PX,
    MODERN_FILE_UPLOAD_DROPZONE_GAP_PX,
    MODERN_FILE_UPLOAD_DROPZONE_PADDING_X_PX,
    MODERN_FILE_UPLOAD_DROPZONE_PADDING_Y_PX,
    MODERN_FILE_UPLOAD_FOREGROUND_COLOR,
    MODERN_FILE_UPLOAD_ICON_BOX_RADIUS_PX,
    MODERN_FILE_UPLOAD_ICON_BOX_SIZE_PX,
    MODERN_FILE_UPLOAD_PROGRESS_HEIGHT_PX,
    MODERN_FILE_UPLOAD_PROGRESS_TRANSITION_MS,
    MODERN_FILE_UPLOAD_ROW_GAP_PX,
    MODERN_FILE_UPLOAD_ROW_MARGIN_TOP_PX,
    resolveFileUploadBrowseButtonBackground,
    resolveFileUploadBrowseButtonForeground,
    resolveFileUploadBrowseButtonHoverBoxShadow,
    resolveFileUploadBrowseButtonRestBoxShadow,
    resolveFileUploadCompletedColor,
    resolveFileUploadDestructiveColor,
    resolveFileUploadDropzoneBackground,
    resolveFileUploadDropzoneBorderColor,
    resolveFileUploadDropzoneIconColor,
    resolveFileUploadIconBoxBackground,
    resolveFileUploadMutedTextColor,
    resolveFileUploadProgressFillColor,
    resolveFileUploadProgressTrackColor,
    resolveFileUploadRowBorderColor,
    modernFileUploadPadding,
    modernFileUploadPaddingTrbl,
    modernFileUploadPaddingYx,
} from "../../../theme/fileUploadThemeTokens";
import { resolveModernButtonFocusBoxShadow } from "../../../theme/navigationButtonThemeColors";

type FileUploadStyleProps = {
    theme: Theme;
    isModern?: boolean;
    isDragOver?: boolean;
    isComplete?: boolean;
    needsProgressBar?: boolean;
};

function getRelativeSize(originalSize: number, theme: Theme) {
    return (Number(theme.TextFontSize) * originalSize) / theme.BaseFontSize;
}

export default createUseStyles({
    fileUploadContainer: {
        composes: "file-upload__container",
        height: ({ theme, isModern }: FileUploadStyleProps) =>
            isModern ? "auto" : getRelativeSize(256, theme),
        border: ({ theme, isModern }: FileUploadStyleProps) =>
            isModern
                ? `${MODERN_FILE_UPLOAD_BORDER_WIDTH_PX}px dashed ${resolveFileUploadDropzoneBorderColor(theme)}`
                : [[3, "dashed", theme.ControlBorderColor]],
        borderRadius: ({ theme }: FileUploadStyleProps) =>
            Math.min(theme.BorderRadius, getRelativeSize(256, theme) / 2),
        marginTop: ({ theme }: FileUploadStyleProps) =>
            Mixins.getSpacing(theme, 0, theme.QuestionSpacing?.WithinComponents),
        textAlign: "center",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: ({ isModern }: FileUploadStyleProps) =>
            isModern ? `${MODERN_FILE_UPLOAD_DROPZONE_GAP_PX}px` : undefined,
        padding: ({ isModern }: FileUploadStyleProps) =>
            isModern
                ? modernFileUploadPaddingYx(
                      MODERN_FILE_UPLOAD_DROPZONE_PADDING_Y_PX,
                      MODERN_FILE_UPLOAD_DROPZONE_PADDING_X_PX,
                  )
                : undefined,
        boxSizing: ({ isModern }: FileUploadStyleProps) =>
            isModern ? "border-box" : undefined,
        background: ({ isModern, isDragOver }: FileUploadStyleProps) =>
            isModern ? resolveFileUploadDropzoneBackground(isDragOver) : undefined,
        "&:focus-within": ({ theme, isModern }: FileUploadStyleProps) =>
            isModern
                ? {
                      borderColor: theme.PrimaryColor,
                  }
                : {},
    },
    fileUploadContainerDrag: {
        composes: "file-upload__container--drag",
        border: ({ theme, isModern }: FileUploadStyleProps) =>
            isModern
                ? `${MODERN_FILE_UPLOAD_BORDER_WIDTH_PX}px dashed ${resolveFileUploadDropzoneBorderColor(theme, true)}`
                : [[3, "dashed", theme.PrimaryColor]],
        background: ({ isModern }: FileUploadStyleProps) =>
            isModern ? resolveFileUploadDropzoneBackground(true) : undefined,
    },
    fileUploadIcon: {
        composes: "file-upload__icon",
        color: ({ theme, isModern }: FileUploadStyleProps) =>
            isModern ? resolveFileUploadDropzoneIconColor() : theme.PrimaryColor,
        margin: [[0, "auto"]],
    },
    fileUploadOptionText: {
        composes: "file-upload__upload-option-text",
        fontWeight: ({ isModern }: FileUploadStyleProps) => (isModern ? 400 : "bolder"),
        margin: ({ isModern }: FileUploadStyleProps) => (isModern ? 0 : [[25, 0]]),
        color: ({ isModern }: FileUploadStyleProps) =>
            isModern ? MODERN_FILE_UPLOAD_FOREGROUND_COLOR : undefined,
        fontSize: ({ theme, isModern }: FileUploadStyleProps) =>
            isModern ? getRelativeSize(16, theme) : undefined,
    },
    fileUploadButton: {
        appearance: "none",
        background: ({ theme, isModern }: FileUploadStyleProps) =>
            isModern
                ? resolveFileUploadBrowseButtonBackground(theme)
                : theme.PrimaryColor,
        border: 0,
        borderRadius: ({ theme }: FileUploadStyleProps) => theme.BorderRadius,
        boxShadow: ({ theme, isModern }: FileUploadStyleProps) =>
            isModern ? resolveFileUploadBrowseButtonRestBoxShadow(theme) : undefined,
        boxSizing: ({ isModern }: FileUploadStyleProps) =>
            isModern ? "border-box" : undefined,
        color: ({ theme, isModern }: FileUploadStyleProps) =>
            isModern
                ? resolveFileUploadBrowseButtonForeground(theme)
                : Mixins.foregroundColor(theme, theme.PrimaryColor),
        composes: "file-upload__button",
        cursor: "pointer",
        font: "inherit",
        fontSize: ({ theme, isModern }: FileUploadStyleProps) =>
            isModern ? getRelativeSize(16, theme) : getRelativeSize(15, theme),
        fontWeight: ({ isModern }: FileUploadStyleProps) => (isModern ? 500 : 700),
        height: ({ isModern }: FileUploadStyleProps) =>
            isModern ? MODERN_FILE_UPLOAD_BROWSE_BUTTON_HEIGHT_PX : undefined,
        padding: ({ isModern }: FileUploadStyleProps) =>
            isModern
                ? modernFileUploadPaddingYx(0, MODERN_FILE_UPLOAD_BROWSE_BUTTON_PADDING_X_PX)
                : [[10, 40]],
        outline: "none",
        transition: ({ isModern }: FileUploadStyleProps) =>
            isModern
                ? "background 200ms ease, box-shadow 200ms ease, color 200ms ease"
                : [["background", "200ms", "ease"]],
        "&:hover": {
            background: ({ theme, isModern }: FileUploadStyleProps) =>
                isModern
                    ? undefined
                    : Mixins.darken(theme.PrimaryColor, theme.ActiveHighlightValue),
            boxShadow: ({ theme, isModern }: FileUploadStyleProps) =>
                isModern
                    ? resolveFileUploadBrowseButtonHoverBoxShadow(theme)
                    : undefined,
        },
        "&:focus-visible": {
            boxShadow: ({ theme, isModern }: FileUploadStyleProps) =>
                isModern ? resolveModernButtonFocusBoxShadow(theme) : undefined,
        },
    },
    fileUploadSelectedFile: {
        background: ({ theme }: FileUploadStyleProps) =>
            Mixins.alpha(theme.ControlBackground, theme.ControlAlpha),
        border: ({ theme, isModern, isComplete }: FileUploadStyleProps) =>
            isModern
                ? `${theme.BorderWidth}px solid ${resolveFileUploadRowBorderColor(theme, isComplete)}`
                : undefined,
        borderRadius: ({ theme }: FileUploadStyleProps) =>
            Math.min(theme.BorderRadius, theme.ChoiceHeight / 2),
        boxShadow: ({ theme, isModern }: FileUploadStyleProps) =>
            isModern
                ? undefined
                : `0 0 0 ${theme.BorderWidth}px ${theme.ControlBorderColor} inset`,
        composes: "file-upload__selected-file",
        marginTop: ({ isModern }: FileUploadStyleProps) =>
            isModern ? MODERN_FILE_UPLOAD_ROW_MARGIN_TOP_PX : 26,
        overflow: "hidden",
        position: "relative",
        padding: ({ isModern, needsProgressBar }: FileUploadStyleProps) =>
            isModern
                ? needsProgressBar
                    ? modernFileUploadPaddingTrbl(12, 12, 16, 12)
                    : modernFileUploadPadding(12)
                : undefined,
    },
    fileUploadFileInfo: {
        composes: "file-upload__file-info",
        height: ({ theme, isModern }: FileUploadStyleProps) =>
            isModern ? "auto" : theme.ChoiceHeight,
        position: "relative",
        padding: ({ isModern }: FileUploadStyleProps) => (isModern ? 0 : [[0, 26]]),
        display: "flex",
        alignItems: ({ isModern }: FileUploadStyleProps) =>
            isModern ? "center" : undefined,
        gap: ({ isModern }: FileUploadStyleProps) =>
            isModern ? `${MODERN_FILE_UPLOAD_ROW_GAP_PX}px` : undefined,
    },
    fileUploadFileContainer: {
        composes: "file-upload__file-container",
        flexGrow: 1,
        minWidth: 0,
    },
    fileUploadFileContainerInner: {
        display: "inline-flex",
        minWidth: 0,
        maxWidth: "100%",
        cursor: "pointer",
        transition: "color 200ms ease",
        outline: "none",
        alignItems: ({ isModern }: FileUploadStyleProps) =>
            isModern ? "center" : undefined,
        gap: ({ isModern }: FileUploadStyleProps) =>
            isModern ? `${MODERN_FILE_UPLOAD_ROW_GAP_PX}px` : undefined,
    },
    fileUploadFileIcon: {
        composes: "file-upload__file-icon",
        display: ({ isModern }: FileUploadStyleProps) =>
            isModern ? "flex" : "inline-block",
        alignItems: ({ isModern }: FileUploadStyleProps) =>
            isModern ? "center" : undefined,
        justifyContent: ({ isModern }: FileUploadStyleProps) =>
            isModern ? "center" : undefined,
        flexShrink: ({ isModern }: FileUploadStyleProps) => (isModern ? 0 : undefined),
        width: ({ isModern }: FileUploadStyleProps) =>
            isModern ? MODERN_FILE_UPLOAD_ICON_BOX_SIZE_PX : undefined,
        height: ({ isModern }: FileUploadStyleProps) =>
            isModern ? MODERN_FILE_UPLOAD_ICON_BOX_SIZE_PX : undefined,
        background: ({ isModern }: FileUploadStyleProps) =>
            isModern ? resolveFileUploadIconBoxBackground() : undefined,
        borderRadius: ({ isModern }: FileUploadStyleProps) =>
            isModern ? MODERN_FILE_UPLOAD_ICON_BOX_RADIUS_PX : undefined,
        color: ({ isModern }: FileUploadStyleProps) =>
            isModern ? MODERN_FILE_UPLOAD_FOREGROUND_COLOR : undefined,
        fontSize: 40,
        lineHeight: ({ theme, isModern }: FileUploadStyleProps) =>
            isModern ? undefined : `${theme.ChoiceHeight}px`,
        marginRight: ({ isModern }: FileUploadStyleProps) => (isModern ? 0 : 20),
        overflow: ({ isModern }: FileUploadStyleProps) =>
            isModern ? "hidden" : undefined,
        "& img": {
            borderRadius: ({ isModern }: FileUploadStyleProps) =>
                isModern ? MODERN_FILE_UPLOAD_ICON_BOX_RADIUS_PX : 3,
            height: ({ isModern }: FileUploadStyleProps) => (isModern ? "100%" : 38),
            objectFit: "cover",
            paddingTop: ({ isModern }: FileUploadStyleProps) => (isModern ? 0 : 10),
            width: ({ isModern }: FileUploadStyleProps) => (isModern ? "100%" : 38),
        },
    },
    fileUploadFileDetails: {
        composes: "file-upload__file-details",
        paddingTop: ({ isModern }: FileUploadStyleProps) => (isModern ? 0 : 10),
        fontSize: ({ theme, isModern }: FileUploadStyleProps) =>
            isModern ? getRelativeSize(16, theme) : getRelativeSize(15, theme),
        lineHeight: "20px",
        display: "inline-block",
        minWidth: 0,
        flexGrow: ({ isModern }: FileUploadStyleProps) => (isModern ? 1 : undefined),
        textAlign: ({ isModern }: FileUploadStyleProps) =>
            isModern ? "left" : undefined,
    },
    fileUploadFileName: {
        composes: "file-upload__file-name",
        fontWeight: ({ isModern }: FileUploadStyleProps) =>
            isModern ? 600 : "bolder",
        textOverflow: "ellipsis",
        overflowX: "hidden",
        whiteSpace: "nowrap",
        color: ({ isModern }: FileUploadStyleProps) =>
            isModern ? MODERN_FILE_UPLOAD_FOREGROUND_COLOR : undefined,
    },
    fileUploadFileSize: {
        composes: "file-upload__file-size",
    },
    fileUploadStatusLine: {
        composes: "file-upload__status-line",
        color: ({ isModern }: FileUploadStyleProps) =>
            isModern ? resolveFileUploadMutedTextColor() : undefined,
        fontSize: ({ theme, isModern }: FileUploadStyleProps) =>
            isModern ? getRelativeSize(16, theme) : undefined,
        fontWeight: 400,
        display: "flex",
        alignItems: "center",
        gap: ({ isModern }: FileUploadStyleProps) => (isModern ? "4px" : 4),
    },
    fileUploadStatusPercent: {
        composes: "file-upload__status-percent",
        fontWeight: 600,
        color: ({ isModern }: FileUploadStyleProps) =>
            isModern ? MODERN_FILE_UPLOAD_FOREGROUND_COLOR : undefined,
    },
    fileUploadCompletedStatus: {
        composes: "file-upload__completed-status",
        display: "flex",
        alignItems: "center",
        gap: ({ isModern }: FileUploadStyleProps) => (isModern ? "4px" : 4),
        color: ({ theme, isModern }: FileUploadStyleProps) =>
            isModern ? resolveFileUploadCompletedColor(theme) : undefined,
        fontWeight: 600,
    },
    fileUploadProgress: {
        background: ({ theme, isModern }: FileUploadStyleProps) =>
            isModern
                ? resolveFileUploadProgressFillColor(theme)
                : theme.SecondaryColor,
        composes: "file-upload__progress",
        height: ({ theme, isModern }: FileUploadStyleProps) =>
            isModern ? MODERN_FILE_UPLOAD_PROGRESS_HEIGHT_PX : theme.ChoiceHeight,
        opacity: ({ isModern }: FileUploadStyleProps) => (isModern ? 1 : 0.15),
        position: "absolute",
        transition: ({ isModern }: FileUploadStyleProps) =>
            isModern
                ? `width ${MODERN_FILE_UPLOAD_PROGRESS_TRANSITION_MS}ms ease`
                : "width 200ms ease 0s",
        zIndex: 2,
        bottom: ({ isModern }: FileUploadStyleProps) => (isModern ? 0 : undefined),
        left: ({ isModern }: FileUploadStyleProps) => (isModern ? 0 : undefined),
        top: ({ isModern }: FileUploadStyleProps) => (isModern ? "auto" : undefined),
    },
    fileUploadProgressTrack: {
        composes: "file-upload__progress-track",
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: MODERN_FILE_UPLOAD_PROGRESS_HEIGHT_PX,
        background: resolveFileUploadProgressTrackColor(),
        zIndex: 1,
    },
    fileUploadProgressText: {
        composes: "file-upload__progress-text",
        flexShrink: 0,
        fontSize: 15,
        fontWeight: "bolder",
        height: "50%",
        margin: [["auto", 30]],
    },
    fileUploadValidationError: {
        color: ({ theme }: FileUploadStyleProps) => theme.ErrorColor,
        composes: "file-upload__validation-error",
        flexShrink: 0,
        fontSize: 15,
        fontWeight: "lighter",
        height: "50%",
        margin: [["auto", 30]],
    },
    fileUploadValidationErrorModern: {
        display: "flex",
        alignItems: "center",
        gap: 4,
        fontSize: ({ theme }: FileUploadStyleProps) =>
            theme.QuestionErrorFontSize != null ? theme.QuestionErrorFontSize : undefined,
        fontWeight: ({ theme }: FileUploadStyleProps) => theme.ErrorFontWeight ?? 700,
        height: "auto",
        lineHeight: "normal",
        margin: 0,
    },
    fileUploadValidationErrorText: {
        lineHeight: "normal",
        position: "relative",
        top: "1px",
    },
    fileUploadValidationErrorIcon: {
        composes: "validation-error__icon",
        color: ({ theme }: FileUploadStyleProps) => theme.ErrorColor,
        flexShrink: 0,
        marginRight: 10,
    },
    fileUploadValidationErrorIconModern: {
        alignSelf: "center",
        display: "block",
        width: 16,
        height: 16,
        marginRight: 0,
    },
    fileUploadDeleteButton: {
        composes: "file-upload__delete-button",
        height: ({ isModern }: FileUploadStyleProps) =>
            isModern ? undefined : "50%",
        margin: ({ isModern }: FileUploadStyleProps) =>
            isModern ? undefined : [["auto", 0]],
        color: ({ theme, isModern }: FileUploadStyleProps) =>
            isModern ? undefined : theme.SecondaryColor,
        cursor: "pointer",
        outline: "none",
        "&:hover": ({ theme, isModern }: FileUploadStyleProps) =>
            isModern
                ? undefined
                : {
                      color: Mixins.darken(theme.SecondaryColor, theme.ActiveHighlightValue),
                  },
        "&:focus-visible": ({ theme, isModern }: FileUploadStyleProps) =>
            isModern
                ? undefined
                : {
                      color: theme.PrimaryColor,
                  },
    },
    /** Modern cancel/delete: matches star-rating clear button (outline focus, grey hover). */
    fileUploadActionButtonModern: {
        composes: "file-upload__delete-button--modern",
        alignItems: "center",
        background: "transparent",
        border: "none",
        borderRadius: MODERN_FILE_UPLOAD_ICON_BOX_RADIUS_PX,
        boxShadow: "none",
        boxSizing: "border-box",
        color: ({ theme, isComplete }: FileUploadStyleProps) =>
            isComplete
                ? resolveFileUploadDestructiveColor(theme)
                : resolveFileUploadMutedTextColor(),
        cursor: "pointer",
        display: "flex",
        flexShrink: 0,
        fontFamily: ({ theme }: FileUploadStyleProps) => `"${theme.TextFont}", sans-serif`,
        height: MODERN_FILE_UPLOAD_ACTION_BUTTON_SIZE_PX,
        justifyContent: "center",
        minHeight: MODERN_FILE_UPLOAD_ACTION_BUTTON_SIZE_PX,
        minWidth: MODERN_FILE_UPLOAD_ACTION_BUTTON_SIZE_PX,
        outline: "none",
        padding: 0,
        width: MODERN_FILE_UPLOAD_ACTION_BUTTON_SIZE_PX,
        transition: "background-color 200ms ease, color 200ms ease",
        "&:hover": {
            background: ({ theme }: FileUploadStyleProps) =>
                Mixins.alpha(theme.NeutralDarkColor, 0.08),
            boxShadow: "none",
        },
        "&:focus-visible": {
            boxShadow: "none",
            outline: ({ theme }: FileUploadStyleProps) =>
                resolveModernChoiceFocusOutline(theme, false),
            outlineOffset: CHOICE_FOCUS_OUTLINE_OFFSET_PX,
        },
        "& .button__container": {
            height: 16,
            lineHeight: "16px",
            margin: 0,
        },
        "& .button__icon": {
            left: 0,
            position: "initial",
            right: 0,
        },
    },
    fileUploadHint: {
        composes: "file-upload__hint",
        fontSize: ({ theme }: FileUploadStyleProps) => getRelativeSize(13, theme),
        marginTop: 8,
        opacity: 0.8,
    },
    srOnly: {
        composes: "sr-only",
        position: "absolute",
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: "hidden",
        clip: "rect(0, 0, 0, 0)",
        whiteSpace: "nowrap",
        border: 0,
    },
} as any);
